import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { XIcon } from 'lucide-react';
interface LegalModalProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  children: React.ReactNode;
}
function LegalModal({
  isOpen,
  onClose,
  title,
  children
}: LegalModalProps) {
  return <AnimatePresence data-id="element-47">
      {isOpen && <>
          {/* Backdrop */}
          <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} transition={{
        duration: 0.25
      }} className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50" onClick={onClose} data-id="element-48" />

          {/* Modal */}
          <motion.div initial={{
        opacity: 0,
        y: 40,
        scale: 0.97
      }} animate={{
        opacity: 1,
        y: 0,
        scale: 1
      }} exit={{
        opacity: 0,
        y: 40,
        scale: 0.97
      }} transition={{
        duration: 0.3,
        ease: 'easeOut'
      }} className="fixed inset-4 md:inset-x-auto md:inset-y-8 md:max-w-2xl md:mx-auto bg-[#1a2342] border border-[#c9a96e]/30 rounded-2xl z-50 flex flex-col overflow-hidden shadow-2xl" data-id="element-49">
            {/* Header */}
            <div className="flex items-center justify-between px-6 py-5 border-b border-[#c9a96e]/20" data-id="element-50">
              <h2 className="font-['Playfair_Display'] text-xl md:text-2xl font-bold text-[#f0ebe3]" data-id="element-51">
                {title}
              </h2>
              <button onClick={onClose} className="w-10 h-10 rounded-full bg-[#0f1629]/60 flex items-center justify-center text-[#a0998e] hover:text-[#f0ebe3] hover:bg-[#0f1629] transition-colors" aria-label="Cerrar" data-id="element-52">
                <XIcon className="w-5 h-5" data-id="element-53" />
              </button>
            </div>

            {/* Content */}
            <div className="flex-1 overflow-y-auto px-6 py-6 text-[#f0ebe3]/80 text-sm leading-relaxed space-y-4" data-id="element-54">
              {children}
            </div>
          </motion.div>
        </>}
    </AnimatePresence>;
}
export function FooterSection() {
  const [showPrivacy, setShowPrivacy] = useState(false);
  const [showTerms, setShowTerms] = useState(false);
  return <>
      <footer className="w-full bg-[#0f1629] border-t border-[#1a2342] py-8" data-id="element-55">
        <div className="max-w-4xl mx-auto px-4 flex flex-col sm:flex-row items-center justify-between gap-4" data-id="element-56">
          <p className="text-[#a0998e] text-sm" data-id="element-57">
            © {new Date().getFullYear()} Breakup Blackout. Todos los derechos
            reservados.
          </p>
          <div className="flex items-center gap-6" data-id="element-58">
            <button onClick={() => setShowPrivacy(true)} className="text-[#a0998e] text-sm hover:text-[#c9a96e] transition-colors" data-id="element-59">
              Política de Privacidad
            </button>
            <span className="text-[#a0998e]/30" data-id="element-60">|</span>
            <button onClick={() => setShowTerms(true)} className="text-[#a0998e] text-sm hover:text-[#c9a96e] transition-colors" data-id="element-61">
              Términos y Condiciones
            </button>
          </div>
        </div>
      </footer>

      {/* Privacy Policy Modal */}
      <LegalModal isOpen={showPrivacy} onClose={() => setShowPrivacy(false)} title="Política de Privacidad" data-id="element-62">
        <p className="text-[#a0998e] text-xs" data-id="element-63">
          Última actualización:{' '}
          {new Date().toLocaleDateString('es-ES', {
          year: 'numeric',
          month: 'long',
          day: 'numeric'
        })}
        </p>

        <h3 className="text-[#c9a96e] font-semibold text-base mt-2" data-id="element-64">
          1. Información que Recopilamos
        </h3>
        <p data-id="element-65">
          Recopilamos únicamente la información necesaria para procesar tu
          compra y entregarte el producto digital. Esto incluye tu dirección de
          correo electrónico y los datos de pago procesados de forma segura a
          través de nuestro proveedor de pagos externo.
        </p>

        <h3 className="text-[#c9a96e] font-semibold text-base" data-id="element-66">
          2. Uso de la Información
        </h3>
        <p data-id="element-67">Tu información personal se utiliza exclusivamente para:</p>
        <ul className="list-disc list-inside space-y-1 text-[#f0ebe3]/70" data-id="element-68">
          <li data-id="element-69">Procesar y entregar tu compra digital.</li>
          <li data-id="element-70">Enviarte el enlace de acceso al producto.</li>
          <li data-id="element-71">Comunicarnos contigo en caso de problemas con tu pedido.</li>
        </ul>

        <h3 className="text-[#c9a96e] font-semibold text-base" data-id="element-72">
          3. Protección de Datos
        </h3>
        <p data-id="element-73">
          Utilizamos encriptación de grado bancario (SSL/TLS) para proteger toda
          la información transmitida a través de nuestro sitio. No almacenamos
          datos de tarjetas de crédito ni información financiera en nuestros
          servidores. Todo el procesamiento de pagos es manejado por proveedores
          certificados PCI-DSS.
        </p>

        <h3 className="text-[#c9a96e] font-semibold text-base" data-id="element-74">
          4. Compartir Información
        </h3>
        <p data-id="element-75">
          No vendemos, intercambiamos ni transferimos tu información personal a
          terceros. La única excepción son los proveedores de servicios de
          confianza que nos asisten en la operación de nuestro sitio web y el
          procesamiento de pagos, siempre bajo acuerdos de confidencialidad.
        </p>

        <h3 className="text-[#c9a96e] font-semibold text-base" data-id="element-76">5. Cookies</h3>
        <p data-id="element-77">
          Nuestro sitio puede utilizar cookies para mejorar tu experiencia de
          navegación y analizar el tráfico del sitio. Puedes configurar tu
          navegador para rechazar cookies, aunque esto podría afectar algunas
          funcionalidades.
        </p>

        <h3 className="text-[#c9a96e] font-semibold text-base" data-id="element-78">
          6. Tus Derechos
        </h3>
        <p data-id="element-79">
          Tienes derecho a solicitar acceso, corrección o eliminación de tus
          datos personales en cualquier momento. Para ejercer estos derechos,
          puedes comunicarte con nosotros a través de los canales disponibles en
          nuestro sitio.
        </p>

        <h3 className="text-[#c9a96e] font-semibold text-base" data-id="element-80">
          7. Cambios a Esta Política
        </h3>
        <p data-id="element-81">
          Nos reservamos el derecho de actualizar esta política de privacidad en
          cualquier momento. Los cambios serán publicados en esta página con la
          fecha de actualización correspondiente.
        </p>
      </LegalModal>

      {/* Terms & Conditions Modal */}
      <LegalModal isOpen={showTerms} onClose={() => setShowTerms(false)} title="Términos y Condiciones" data-id="element-82">
        <p className="text-[#a0998e] text-xs" data-id="element-83">
          Última actualización:{' '}
          {new Date().toLocaleDateString('es-ES', {
          year: 'numeric',
          month: 'long',
          day: 'numeric'
        })}
        </p>

        <h3 className="text-[#c9a96e] font-semibold text-base mt-2" data-id="element-84">
          1. Aceptación de los Términos
        </h3>
        <p data-id="element-85">
          Al acceder y utilizar este sitio web y al realizar una compra, aceptas
          estar sujeta a estos términos y condiciones. Si no estás de acuerdo
          con alguna parte de estos términos, te pedimos que no utilices
          nuestros servicios.
        </p>

        <h3 className="text-[#c9a96e] font-semibold text-base" data-id="element-86">
          2. Naturaleza del Producto
        </h3>
        <p data-id="element-87">
          "Breakup Blackout: Las Primeras 72 Horas" es un producto digital de
          carácter informativo y educativo. No constituye asesoramiento médico,
          psicológico ni terapéutico profesional. Si estás experimentando una
          crisis de salud mental, te recomendamos buscar ayuda profesional de
          inmediato.
        </p>

        <h3 className="text-[#c9a96e] font-semibold text-base" data-id="element-88">
          3. Entrega del Producto
        </h3>
        <p data-id="element-89">
          Al completar tu compra, recibirás acceso instantáneo al contenido
          digital a través de un enlace de descarga. El acceso es inmediato y
          está disponible las 24 horas del día, los 7 días de la semana.
        </p>

        <h3 className="text-[#c9a96e] font-semibold text-base" data-id="element-90">
          4. Política de Reembolso
        </h3>
        <p data-id="element-91">
          Debido a la naturaleza digital e instantánea del producto, todas las
          ventas son finales. Una vez que se ha entregado el acceso al contenido
          digital, no se procesarán reembolsos. Te recomendamos leer
          cuidadosamente la descripción del producto antes de realizar tu
          compra.
        </p>

        <h3 className="text-[#c9a96e] font-semibold text-base" data-id="element-92">
          5. Propiedad Intelectual
        </h3>
        <p data-id="element-93">
          Todo el contenido incluido en "Breakup Blackout: Las Primeras 72
          Horas" — incluyendo textos, gráficos, audios y materiales descargables
          — está protegido por derechos de autor. Queda prohibida la
          reproducción, distribución o reventa del contenido sin autorización
          expresa por escrito.
        </p>

        <h3 className="text-[#c9a96e] font-semibold text-base" data-id="element-94">
          6. Uso Personal
        </h3>
        <p data-id="element-95">
          El producto es para uso personal e individual. No está permitido
          compartir, copiar, distribuir o revender el contenido a terceros. Cada
          compra otorga una licencia de uso personal e intransferible.
        </p>

        <h3 className="text-[#c9a96e] font-semibold text-base" data-id="element-96">
          7. Resultados
        </h3>
        <p data-id="element-97">
          Los resultados mencionados en nuestra página de ventas son basados en
          experiencias reportadas por usuarias. Los resultados individuales
          pueden variar. No garantizamos resultados específicos, ya que estos
          dependen de múltiples factores individuales y del compromiso con el
          protocolo.
        </p>

        <h3 className="text-[#c9a96e] font-semibold text-base" data-id="element-98">
          8. Limitación de Responsabilidad
        </h3>
        <p data-id="element-99">
          Breakup Blackout y sus creadores no serán responsables por daños
          directos, indirectos, incidentales o consecuentes que resulten del uso
          o la incapacidad de usar el producto. El uso del contenido es bajo tu
          propia responsabilidad.
        </p>

        <h3 className="text-[#c9a96e] font-semibold text-base" data-id="element-100">
          9. Modificaciones
        </h3>
        <p data-id="element-101">
          Nos reservamos el derecho de modificar estos términos y condiciones en
          cualquier momento. Las modificaciones entrarán en vigor inmediatamente
          después de su publicación en el sitio web.
        </p>
      </LegalModal>
    </>;
}